import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './fonts.css';
import CssBaseline from '@mui/material/CssBaseline';
import ThankYou from './features/ThankYou/ThankYou';
import { GlobalProvider } from './contexts/GlobalProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { Login } from './features/Login';
import DynamicMemberPortal from './features/MemberPortal/DynamicMemberPortal/DynamicMemberPortal';
import ArenaGamePage from './features/TcgArena';
import { MigrateBillingPrompt } from './features/MemberPortal/DynamicMemberPortal/components/MigrateBillingPrompt/MigrateBillingPrompt';
import { SignUp } from './features/SignUp';
import { SetPassword } from './features/SetPassword';
import { TwoFactorResetRequest, TwoFactorResetCancel } from './features/TwoFactorReset';
import { PublicPhysicalCards } from './features/PhysicalCards/PublicPhysicalCards';
import { SnackbarProvider } from 'notistack';
import Checkout from './features/Home/DynamicHomepage/components/Checkout/Checkout';
import { RouteAnalytics } from './utils/googleAnalyticsRouteAnalytics';
import { Analytics } from './utils/Analytics';
import PdfView from './features/Home/DynamicHomepage/components/PDFViewer/PDFViewer';
import RafflePage from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePage';
import RafflePaymentPage from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentPage';
import DynamicHomepage from './features/Home/DynamicHomepage/DynamicHomepage';
import RafflePaymentConfirmationPage from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentConfirmationPage';
import GiveawayUpsellPage from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RaffleUpsellPage';
import GiveawaySubsPage from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/GiveawaySubsPage';
import {
  GiveawayOfferPage,
  GiveawayOfferConfirmationPage,
  GiveawayOfferSubscribedPage,
  GiveawayOfferGate
} from './features/GiveawayOffer';
import {
  PromoPackPage,
  PromoPackConfirmationPage
} from './features/PromoPack';
import GiveawayPixels from './utils/GiveawayPixels';
import EmailPromo from './features/Home/DynamicHomepage/components/NewLayout/RafflePage/EmailPromo/EmailPromo';
import Roadmap from './features/Home/DynamicHomepage/components/Roadmap/Roadmap';
import { MaintenanceGate } from './features/Maintenance';
import DesignSystemPage from './features/DesignSystem';
import DesignGiveawayPage from './features/DesignSystem/DesignGiveawayPage';
import ContactPage from './features/ContactPage';
import PullPage from './features/PullPage/PullPage';
import DigitalCollectibles from './features/DigitalCollectibles';
import TradingCardGame from './features/TradingCardGame';
import PartnerTestPage from './features/PartnerTest/PartnerTestPage';
// DEV-ONLY: preview the unclaimed-packs unpacking flow (see /pack-dev route below).
import { UnclaimedPacksWidget } from './features/MemberPortal/DynamicMemberPortal/components/UnclaimedPacksWidget/UnclaimedPacksWidget';

// Change for testing amplify previews again
const theme = createTheme({
  palette: {
    mode: 'dark'
  },
  typography: {
    fontFamily: ['Montserrat', 'sans-serif'].join(','),
    allVariants: {
      color: '#ffffff'
    }
  },
  components: {
    MuiTypography: {
      defaultProps: {
        color: 'textPrimary'
      }
    }
  }
});

const EmailPromoRoute = () => {
  const location = useLocation();
  const discountCode = new URLSearchParams(location.search)
    .get('discount')
    ?.trim();

  if (!discountCode) {
    return <Navigate to="/" replace />;
  }

  return <EmailPromo />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalProvider>
        <SnackbarProvider
          maxSnack={3} // Set maximum number of snackbars displayed at once
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Set the position of the snackbars
        >
          <MaintenanceGate>
            <RouteAnalytics />
            <Analytics />
            {/* <MetaPixelRoutes />
          <TikTokPixelRoutes /> */}
            <GiveawayPixels />
            <Routes>
              {/* DEV-ONLY pack-open preview — renders the real UnclaimedPacksWidget,
                  ungated. A mock API on :4242 feeds it fake packs. Remove before commit. */}
              <Route
                path="/pack-dev"
                element={
                  <div style={{ maxWidth: 460, margin: '48px auto', padding: '0 16px' }}>
                    <UnclaimedPacksWidget />
                  </div>
                }
              />
              <Route path="/" element={<DynamicHomepage />} />
              <Route path="/design" element={<DesignSystemPage />} />
              <Route path="/design/giveaway" element={<DesignGiveawayPage />} />
              <Route path="/giveaway" element={<RafflePage />} />
              <Route path="/giveaway-subs" element={<GiveawaySubsPage />} />
              {/* The QR / poster funnel. A free, no-purchase signup that
                  claims entries into the jackpot draw; the entries themselves
                  are granted on email verification, not here. Unlike
                  /partner-signup this form makes no /auth/check-* probes. */}
              <Route path="/giveaway-offer" element={<GiveawayOfferPage />} />
              <Route
                path="/giveaway-offer/confirmation"
                element={<GiveawayOfferConfirmationPage />}
              />
              {/* Subscriptions bought from the funnel land here rather than on
                  /giveaway-subs/confirmation: that screen is shared by three
                  routes and styled for the light giveaway funnel. */}
              <Route
                path="/giveaway-offer/subscribed"
                element={<GiveawayOfferSubscribedPage />}
              />
              {/* <Route
                path="/giveaway-subs/checkout"
                element={<Navigate to="/giveaway-subs" replace />}
              /> */}
              <Route
                path="/giveaway-subs/confirmation"
                element={<RafflePaymentConfirmationPage />}
              />
              <Route
                path="/giveaway/special-offer"
                element={<RafflePaymentPage />}
              />
              <Route path="/giveaway/upsell" element={<GiveawayUpsellPage />} />
              <Route
                path="/giveaway/confirmation-one-off"
                element={<RafflePaymentConfirmationPage />}
              />
              <Route
                path="/giveaway/confirmation-sub"
                element={<RafflePaymentConfirmationPage />}
              />
              {/* This week's promo pack, sold to logged-out visitors through the
                  giveaway's one-off guest checkout (RaffleEntryForm, then
                  RaffleStripeCheckoutForm); a paid one lands on its own
                  confirmation, not the upsell page. Static, so it outranks
                  /:offerSlug/* — and `promo` must also be in RESERVED in
                  server + admin-server lib/qrPageSlug.js. */}
              <Route path="/promo" element={<PromoPackPage />} />
              <Route
                path="/promo/confirmation"
                element={<PromoPackConfirmationPage />}
              />

              <Route path="/privacy" element={<PdfView />} />
              <Route path="/checkout" element={<Checkout />} />
              {/* /exclusive-offer is retired. Nothing navigated to it — no link, no
                  return_url — and it is the one upsell page that reads the payment
                  intent id from its own URL, which is exactly the handoff the signed
                  token replaced. It could still render an offer and then fail the
                  charge, so it goes to the confirmation instead of dead-ending.
                  features/Upsell is still in the tree. */}
              <Route path="/exclusive-offer" element={<Navigate to="/thank-you" replace />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/login" element={<Login />} />
              <Route path="/sign-up" element={<SignUp />} />
              {/* Partner / special signup is OFF for now.
                  Its form probed /auth/check-email to decide what to show, which
                  told anyone who typed an address whether we already had it. Rather
                  than rework a funnel that is not currently running, the routes send
                  people to the giveaway — existing links keep working instead of
                  404ing, and the surface is gone. The pages are still in the tree
                  (features/PartnerSignup) ready to be turned back on. */}
              <Route path="/partner-signup" element={<Navigate to="/giveaway" replace />} />
              <Route path="/special-signup" element={<Navigate to="/giveaway" replace />} />
              {/* <Route path="/partner-test" element={<PartnerTestPage />} />
              <Route
                path="/partner-test/upsell"
                element={<PartnerTestPage />}
              /> */}
              <Route
                path="/partner-signup/upgrade"
                element={<Navigate to="/giveaway" replace />}
              />
              <Route
                path="/special-signup/upgrade"
                element={<Navigate to="/giveaway" replace />}
              />
              <Route
                path="/partner-signup/confirmation"
                element={<Navigate to="/giveaway" replace />}
              />
              <Route
                path="/special-signup/confirmation"
                element={<Navigate to="/giveaway" replace />}
              />
              <Route path="/set-password" element={<SetPassword />} />
              {/* Lost authenticator + lost recovery codes: ask for a 2FA reset, and the
                  one-click cancel from the email. "account" is a reserved QR slug on the
                  server, so no poster page can ever take this path. */}
              <Route path="/account/2fa-reset" element={<TwoFactorResetRequest />} />
              <Route path="/account/2fa-reset/cancel" element={<TwoFactorResetCancel />} />
              <Route path="/email-promo" element={<EmailPromoRoute />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/trading-card-game" element={<TradingCardGame />} />
              <Route path="/digital-collectibles" element={<DigitalCollectibles />} />
              <Route path="/physical-cards" element={<PublicPhysicalCards />} />
              <Route path="/contact-us" element={<ContactPage />} />
              {/* A shared pack pull: public, the token is the only key (server /pulls/:token). */}
              <Route path="/pull/:token" element={<PullPage />} />
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
              {/* Legacy dev URL — the game now lives at /member-portal/play. */}
              <Route
                path="/admin/tcg-test"
                element={<Navigate to="/member-portal/play" replace />}
              />
              {/* DEV-ONLY test-harness route. Renders ArenaGamePage standalone (no
                  portal shell, no ProtectedRoute) so the browser rig can exercise the
                  real client's socket layer in isolation. Safe: NODE_ENV is inlined at
                  build time, so a production build (`craco build`) evaluates this to
                  `false` and the route is dead-code-eliminated — it can never reach a
                  real user. It carries no auth of its own either: with no injected
                  session, ArenaGamePage cannot mint a game token, so it connects to
                  nothing and shows nothing. Present only in local dev builds. */}
              {process.env.NODE_ENV !== 'production' && (
                <Route
                  path="/tcg-arena-test"
                  // embedded renders the arena at height:100%, which needs a real height
                  // to resolve against — the portal shell supplies one (DynamicMemberPortal,
                  // contentHeight), but this standalone route must give it one itself or the
                  // whole chain collapses and the game renders in a short band at the top.
                  element={
                    <div style={{ width: '100%', height: '100dvh', overflow: 'hidden' }}>
                      <ArenaGamePage embedded requireAdmin={false} />
                    </div>
                  }
                />
              )}
              {/* DEV-ONLY preview of the billing-migration prompt.
                  It cannot be seen on its real trigger locally: the modal opens
                  only when the server says needsMigration, and the server answers
                  that through assessLegacyMigration, which asks Stripe about the
                  member's LEGACY subscription — with test keys there is nothing
                  to find, so the answer is always no. This renders the same
                  component with the fetch skipped, purely to look at it.
                  NODE_ENV is inlined at build time, so `craco build` evaluates
                  this to false and the route is dead-code-eliminated. */}
              {process.env.NODE_ENV !== 'production' && (
                <Route
                  path="/preview/migrate-billing"
                  element={<MigrateBillingPrompt enabled previewOpen />}
                />
              )}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/member-portal/*"
                  element={<DynamicMemberPortal />}
                />
              </Route>
              {/* The QR funnel on the paths an admin picks (/token2049-offer and
                  its /confirmation and /subscribed) with no client deploy: the
                  gate asks GET /giveaway/offer/pages which slugs exist, and
                  renders any other path exactly as the catch-all below does.

                  IT CANNOT SHADOW A ROUTE ABOVE. React Router takes the most
                  specific match, not the first: a static segment scores 10, a
                  dynamic one 3, and a splat takes 2 off. This scores 5; every
                  route above that can match the same URL scores 12 or more
                  (/login 13, /member-portal/* 12), so it only ever sees what
                  would otherwise fall through to `*`, which scores 1.

                  ONE SPLAT, not /:offerSlug/confirmation and /:offerSlug/subscribed:
                  those score 17 and would take /member-portal/confirmation and
                  /member-portal/subscribed from the portal. The gate reads the
                  rest of the path itself. /giveaway-offer keeps its static routes
                  above, so the posters already printed never wait on the lookup. */}
              <Route
                path="/:offerSlug/*"
                element={<GiveawayOfferGate fallback={<DynamicHomepage />} />}
              />
              <Route path="*" element={<DynamicHomepage />} />
            </Routes>
          </MaintenanceGate>
        </SnackbarProvider>
      </GlobalProvider>
    </ThemeProvider>
  );
}

export default App;
